import React, { useState, useEffect } from 'react';
import { Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import '../../styles/realsignal-theme.css';

interface AuthPageProps {
    onLogin?: (email: string, password: string, rememberMe: boolean) => void;
    onGoogleLogin?: () => void;
    onForgotPassword?: () => void;
    onActivateAccount?: () => void;
    onTermsClick?: () => void;
    onPrivacyClick?: () => void;
    error?: string;
    isLoading?: boolean;
}

const colors = {
    background: '#0F1A24',
    backgroundLight: '#162434',
    backgroundCard: '#1C2E42',
    backgroundWell: '#0A1218',
    textPrimary: '#E6EBF0',
    textSecondary: '#A9B4C2',
    textMuted: '#7F8C9C',
    accent: '#3A6A7A',
    accentLight: '#4A8A9A',
    accentDark: '#2E4A5A',
    border: '#1C2E42',
};

export const AuthPage: React.FC<AuthPageProps> = ({
    onLogin,
    onGoogleLogin,
    onForgotPassword,
    onActivateAccount,
    onTermsClick,
    onPrivacyClick,
    error,
    isLoading,
}) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 50);
        return () => clearTimeout(timer);
    }, []);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onLogin?.(email, password, rememberMe);
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center relative overflow-hidden"
            style={{
                background: colors.background,
                fontFamily:
                    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                opacity: isVisible ? 1 : 0,
                transition: 'opacity 500ms ease-in',
            }}
        >
            {/* Texture overlay - grainy noise blended with navy background */}
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: 'url(/assets/realsignal-bg-texture.jpg)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    opacity: 0.12,
                    mixBlendMode: 'luminosity',
                    filter: 'grayscale(100%)',
                    pointerEvents: 'none',
                }}
            />
            <div className="relative z-10 w-full max-w-md mx-auto px-6">
                {/* Logo and tagline */}
                <div className="text-center mb-6">
                    <div className="mb-4">
                        <div className="flex justify-center mb-2">
                            <div
                                style={{
                                    position: 'relative',
                                    height: '48px',
                                    width: '200px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <img
                                    src="/assets/realsignal-logo.png"
                                    alt="RealSignal"
                                    className="h-12 w-auto"
                                    style={{
                                        position: 'absolute',
                                        filter: 'brightness(1.15)',
                                        animation: 'logoMorphOut 10s ease-in-out infinite',
                                    }}
                                />
                                <img
                                    src="/assets/realsignal-logo-white.png"
                                    alt="RealSignal"
                                    className="h-12 w-auto"
                                    style={{
                                        position: 'absolute',
                                        animation: 'logoMorphIn 10s ease-in-out infinite',
                                    }}
                                />
                            </div>
                        </div>
                        <p style={{ color: colors.textSecondary, fontSize: '1.125rem' }}>
                            Real Estate Distress Intelligence
                        </p>
                    </div>
                </div>
                <style>{`
                    @keyframes logoMorphOut {
                        0%, 35% { opacity: 1; }
                        45%, 85% { opacity: 0; }
                        95%, 100% { opacity: 1; }
                    }
                    @keyframes logoMorphIn {
                        0%, 35% { opacity: 0; }
                        45%, 85% { opacity: 1; }
                        95%, 100% { opacity: 0; }
                    }
                `}</style>

                {/* Auth Card with animated border */}
                <div
                    style={{
                        position: 'relative',
                        borderRadius: '1.5rem',
                        padding: '2px',
                    }}
                >
                    {/* Animated light trace border */}
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: '1.5rem',
                            background: `conic-gradient(from var(--card-angle, 0deg), transparent 0deg, ${colors.accentLight} 15deg, transparent 90deg)`,
                            animation: 'cardTrace 6s linear infinite',
                            opacity: 0.7,
                        }}
                    />
                    {/* Card background overlay to create border effect */}
                    <div
                        style={{
                            position: 'absolute',
                            inset: '2px',
                            borderRadius: 'calc(1.5rem - 2px)',
                            background: `linear-gradient(145deg, ${colors.backgroundCard} 0%, ${colors.backgroundLight} 100%)`,
                        }}
                    />
                    {/* Card content */}
                    <div
                        style={{
                            position: 'relative',
                            background: `linear-gradient(145deg, ${colors.backgroundCard} 0%, ${colors.backgroundLight} 100%)`,
                            borderRadius: 'calc(1.5rem - 2px)',
                            padding: '1.5rem',
                            boxShadow:
                                '0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 12px 24px -8px rgba(0, 0, 0, 0.5)',
                            border: '1px solid rgba(255, 255, 255, 0.08)',
                        }}
                    >
                        <div className="text-center mb-4">
                            <h2
                                style={{
                                    fontSize: '1.25rem',
                                    fontWeight: 700,
                                    color: colors.textPrimary,
                                    marginBottom: '0.25rem',
                                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                                }}
                            >
                                Welcome to RealSignal
                            </h2>
                            <p style={{ color: colors.textSecondary }}>
                                Sign in to access foreclosure data
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div
                                style={{
                                    background: colors.backgroundWell,
                                    borderRadius: '0.75rem',
                                    padding: '0.75rem',
                                    border: `1px solid ${colors.border}`,
                                }}
                                className="space-y-3"
                            >
                                {/* Email Field */}
                                <div>
                                    <label
                                        style={{
                                            display: 'block',
                                            fontSize: '0.875rem',
                                            fontWeight: 500,
                                            color: colors.textSecondary,
                                            marginBottom: '0.5rem',
                                        }}
                                    >
                                        Email Address
                                    </label>
                                    <div className="relative">
                                        <Mail
                                            style={{
                                                position: 'absolute',
                                                left: '0.75rem',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                color: colors.textMuted,
                                                width: '1.25rem',
                                                height: '1.25rem',
                                                zIndex: 10,
                                            }}
                                        />
                                        <input
                                            type="email"
                                            name="email"
                                            placeholder="investor@company.com"
                                            value={email}
                                            onChange={e => setEmail(e.target.value)}
                                            style={{
                                                width: '100%',
                                                background: 'rgba(0, 0, 0, 0.3)',
                                                border: '1px solid rgba(255, 255, 255, 0.08)',
                                                borderRadius: '0.75rem',
                                                padding: '0.75rem 1rem 0.75rem 2.75rem',
                                                color: colors.textPrimary,
                                                fontSize: '0.875rem',
                                                boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.3)',
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Password Field */}
                                <div>
                                    <label
                                        style={{
                                            display: 'block',
                                            fontSize: '0.875rem',
                                            fontWeight: 500,
                                            color: colors.textSecondary,
                                            marginBottom: '0.5rem',
                                        }}
                                    >
                                        Password
                                    </label>
                                    <div className="relative">
                                        <Lock
                                            style={{
                                                position: 'absolute',
                                                left: '0.75rem',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                color: colors.textMuted,
                                                width: '1.25rem',
                                                height: '1.25rem',
                                                zIndex: 10,
                                            }}
                                        />
                                        <input
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            placeholder="Enter your password"
                                            value={password}
                                            onChange={e => setPassword(e.target.value)}
                                            style={{
                                                width: '100%',
                                                background: 'rgba(0, 0, 0, 0.3)',
                                                border: '1px solid rgba(255, 255, 255, 0.08)',
                                                borderRadius: '0.75rem',
                                                padding: '0.75rem 3rem 0.75rem 2.75rem',
                                                color: colors.textPrimary,
                                                fontSize: '0.875rem',
                                                boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.3)',
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            style={{
                                                position: 'absolute',
                                                right: '0.75rem',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                color: colors.textMuted,
                                                background: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                zIndex: 10,
                                            }}
                                        >
                                            {showPassword ? (
                                                <EyeOff className="w-5 h-5" />
                                            ) : (
                                                <Eye className="w-5 h-5" />
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Remember me & Forgot */}
                            <div className="flex items-center justify-between">
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={rememberMe}
                                        onChange={e => setRememberMe(e.target.checked)}
                                        style={{
                                            borderRadius: '0.25rem',
                                            borderColor: colors.border,
                                            background: colors.background,
                                        }}
                                    />
                                    <span
                                        style={{
                                            marginLeft: '0.5rem',
                                            fontSize: '0.875rem',
                                            color: colors.textSecondary,
                                        }}
                                    >
                                        Remember me
                                    </span>
                                </label>
                                <button
                                    type="button"
                                    onClick={onForgotPassword}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: colors.accentLight,
                                        fontWeight: 500,
                                        fontSize: '0.875rem',
                                        cursor: 'pointer',
                                    }}
                                >
                                    Forgot?
                                </button>
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div
                                    style={{
                                        background: 'rgba(220, 38, 38, 0.1)',
                                        border: '1px solid rgba(220, 38, 38, 0.3)',
                                        borderRadius: '0.5rem',
                                        padding: '0.75rem 1rem',
                                        color: '#FCA5A5',
                                        fontSize: '0.875rem',
                                        marginBottom: '1rem',
                                    }}
                                >
                                    {error}
                                </div>
                            )}

                            {/* Sign In Button */}
                            <button
                                type="submit"
                                disabled={isLoading}
                                style={{
                                    width: '100%',
                                    background: isLoading
                                        ? colors.accentDark
                                        : `linear-gradient(180deg, ${colors.accentLight} 0%, ${colors.accent} 50%, ${colors.accentDark} 100%)`,
                                    color: 'white',
                                    fontWeight: 600,
                                    padding: '1rem 1.5rem',
                                    borderRadius: '1.5rem',
                                    border: `1px solid rgba(255, 255, 255, 0.15)`,
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '0.5rem',
                                    boxShadow:
                                        '0 8px 20px rgba(0, 0, 0, 0.4), 0 4px 8px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2), inset 0 -1px 0 rgba(0, 0, 0, 0.2)',
                                    opacity: isLoading ? 0.7 : 1,
                                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                                }}
                            >
                                <span>{isLoading ? 'Signing In...' : 'Sign In'}</span>
                                {!isLoading && <ArrowRight className="w-5 h-5" />}
                            </button>
                        </form>

                        {/* Sign Up */}
                        <div className="mt-4 text-center">
                            <p style={{ color: colors.textSecondary }}>
                                Don't have an account?
                                <button
                                    onClick={onActivateAccount}
                                    style={{
                                        marginLeft: '0.5rem',
                                        color: colors.accentLight,
                                        fontWeight: 500,
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                    }}
                                >
                                    Sign up
                                </button>
                            </p>
                        </div>

                        {/* Divider */}
                        <div className="mt-4">
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <div
                                        style={{
                                            width: '100%',
                                            borderTop: `1px solid ${colors.border}`,
                                        }}
                                    />
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span
                                        style={{
                                            padding: '0 0.5rem',
                                            background: colors.backgroundLight,
                                            color: colors.textSecondary,
                                        }}
                                    >
                                        Or continue with
                                    </span>
                                </div>
                            </div>

                            {/* Social Login Icons */}
                            <div className="mt-6 flex justify-center gap-4">
                                {/* Google */}
                                <button
                                    title="Sign in with Google"
                                    onClick={onGoogleLogin}
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        color: colors.textSecondary,
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                        borderRadius: '50%',
                                        width: '48px',
                                        height: '48px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow:
                                            '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                                        <path
                                            fill="currentColor"
                                            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                        />
                                        <path
                                            fill="currentColor"
                                            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                        />
                                        <path
                                            fill="currentColor"
                                            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                        />
                                        <path
                                            fill="currentColor"
                                            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                        />
                                    </svg>
                                </button>
                                {/* Facebook */}
                                <button
                                    title="Sign in with Facebook"
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        color: colors.textSecondary,
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                        borderRadius: '50%',
                                        width: '48px',
                                        height: '48px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow:
                                            '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <svg
                                        className="w-5 h-5"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                    >
                                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                                    </svg>
                                </button>
                                {/* Apple */}
                                <button
                                    title="Sign in with Apple"
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        color: colors.textSecondary,
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                        borderRadius: '50%',
                                        width: '48px',
                                        height: '48px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow:
                                            '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <svg
                                        className="w-5 h-5"
                                        viewBox="0 0 24 24"
                                        fill="currentColor"
                                    >
                                        <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <p
                    style={{
                        marginTop: '1rem',
                        textAlign: 'center',
                        fontSize: '0.75rem',
                        color: colors.textMuted,
                    }}
                >
                    By continuing, you agree to our{' '}
                    <button
                        onClick={onTermsClick}
                        style={{
                            color: colors.accentLight,
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                        }}
                    >
                        Terms of Service
                    </button>{' '}
                    and{' '}
                    <button
                        onClick={onPrivacyClick}
                        style={{
                            color: colors.accentLight,
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                        }}
                    >
                        Privacy Policy
                    </button>
                </p>
            </div>

            {/* CSS Keyframes for light trace animation */}
            <style>{`
                @property --card-angle {
                    syntax: '<angle>';
                    initial-value: 0deg;
                    inherits: false;
                }
                @keyframes cardTrace {
                    from { --card-angle: 0deg; }
                    to { --card-angle: 360deg; }
                }
            `}</style>
        </div>
    );
};

export default AuthPage;
